import type { CompositeWellboreLeg } from 'components/composite-leg-lines/types';
import type { SlotOverrideOperation, SlotRow } from './types';

/**
 * The row the Volume Source drawer edits when a composite wellbore's LEG is being overridden:
 * the composite's row re-keyed to the leg — its own slot id (what the override is stored
 * against), its formation (what the drawer's curve list is scoped by) and its current curve.
 * The override is the volume source only, so everything else stays the well's.
 *
 * @param {T} row - The composite's row.
 * @param {CompositeWellboreLeg} leg - The leg being edited.
 * @return {T} The leg as a row.
 */
export function legEditRow<T extends SlotRow>(
  row: T,
  leg: CompositeWellboreLeg
): T {
  return {
    ...row,
    slotId: leg.slotId,
    formation: leg.formation ?? undefined,
    volumeSourceId: leg.volumeSourceId ?? undefined,
    volumeSourceHref: leg.volumeSourceHref ?? undefined,
    // The row's own name and type describe the whole recipe, not the leg's curve, so both are
    // cleared; the drawer names a pinned source by its id when it has no name.
    volumeSourceName: undefined,
    volumeSourceType: undefined,
    forecastStatus: leg.forecastStatus ?? undefined,
    overriddenFields: leg.overridden ? ['volumeSourceId'] : [],
  };
}

/**
 * Whether a slot operation names a composite wellbore's leg rather than a row: no row carries
 * its slot id, and some row's legs do. Such an operation is painted onto the leg
 * (`paintNewestOperation`), but the row's recipe is the server's to fold, so the grid refetches.
 *
 * @param {SlotRow[]} rows - The rows on screen.
 * @param {SlotOverrideOperation} operation - The operation about to be committed.
 * @return {boolean} True when the operation targets a leg.
 */
export function targetsCompositeLeg(
  rows: SlotRow[],
  operation: SlotOverrideOperation
): boolean {
  if (operation.kind !== 'slot') {
    return false;
  }
  return (
    !rows.some((row) => String(row.slotId) === operation.slotId) &&
    rows.some((row) =>
      row.compositeWellboreLegs?.some((leg) => leg.slotId === operation.slotId)
    )
  );
}
